/**
 * orders-api — typed wrappers for the admin ORDERS / TRANSACTIONS surface,
 * through the @v0y4ge/api-client `apiRaw` choke point (same seam as
 * payments-api / appointments-api / wf-api).
 *
 * These admin ops (`/v1/admin/orders*`, `/v1/admin/refunds`) are DECLARED
 * (the buyer-side money path already ships — REQ-036/058/060); until they land
 * in @v0y4ge/api-client + api-mocks the preview harness serves them from the
 * `__source`-marked seed layer (orders-preview-seeds.js), surfaced via
 * <DataSourceStrip>. When each op ships for real, its chip disappears with zero
 * UI changes.
 *
 * Money rules (REQ-036 posture): the FE never computes an amount. Every amount
 * rendered comes from an op response (minor units). A refund names only the
 * order + an optional partial amount + reason; the server derives the payment,
 * connected account, and application-fee reversal itself.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

/** Data-source transparency (2026-07-16 convention): report every op result. */
const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

const q = (params: Record<string, string | undefined | null>): string => {
  const s = Object.entries(params)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  return s ? `?${s}` : "";
};

export type OrderStatus = "pending" | "paid" | "reserved" | "refunded" | "partially_refunded" | "cancelled" | "failed" | "disputed";
export type OrderSource = "event" | "appointment" | "walkin";
export type OrderProvider = "stripe" | "paypal" | "adyen" | null;

export interface OrderLineItem {
  id: string;
  description: string;
  amountMinor: number;
  itemType: "booking" | "addon" | "fee" | "discount";
}

export interface Order {
  id: string;
  ref: string;                    // human order reference (e.g. JRNI-…)
  paymentId: string | null;       // the payment the refund op operates on
  customerName: string;
  customerEmail?: string;
  source: OrderSource;
  sourceLabel: string;            // event title / service name / walk-in
  provider: OrderProvider;
  totalMinor: number;
  refundedMinor: number;          // 0 unless (partially) refunded
  currency: string;
  status: OrderStatus;
  createdAt: string;              // ISO
  paymentRef?: string | null;     // provider reference (pi_… / order id / psp)
  receiptUrl?: string | null;     // provider-hosted receipt, when available
  items?: OrderLineItem[];
}

export interface OrdersKpis {
  grossMinor: number;
  refundedMinor: number;
  netMinor: number;
  count: number;
  currency: string;
  /** On-site "reserved" money owed but NOT yet collected — never summed into gross/net. */
  reservedMinor?: number;
  reservedCount?: number;
}

export type CollectMethod = "card_present" | "cash" | "card_on_file" | "other";

export interface CollectResult {
  orderId: string;
  status: OrderStatus;      // → "paid"
  collectedMinor: number;
  method?: CollectMethod;
  paymentRef?: string | null;
}

export interface OrdersPage {
  orders: Order[];
  total: number;          // full filtered count (all pages)
  page?: number;          // 1-based current page
  pageSize?: number;
  pages?: number;         // total page count
  kpis: OrdersKpis;       // computed over the FULL filtered set, stable across pages
}

export interface RefundResult {
  refundId: string;
  status: "pending" | "succeeded" | "failed";
  amountMinor: number;
  orderStatus: OrderStatus;
}

/* ── Response mapping (CC 0185) ─────────────────────────────────────────────
   The orders operations answer the contract in snake_case (gross_minor,
   page_size, customer_name, …), and getAdminOrder wraps the order as { order }.
   One mapper per shape turns that into the screen's camelCase types. Each reads
   the contract name first and the camelCase name second, so the preview seeds
   (camelCase) keep working. A missing money field maps to 0, never NaN. */
type Raw = Record<string, unknown>;
const pick = (r: Raw, snake: string, camel: string): unknown => (r[snake] !== undefined ? r[snake] : r[camel]);
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const optNum = (v: unknown): number | undefined => (v == null ? undefined : num(v));
const str = (v: unknown): string => (v == null ? "" : String(v));
const optStr = (v: unknown): string | null | undefined => (v === undefined ? undefined : v === null ? null : String(v));

function mapLineItem(raw: unknown): OrderLineItem {
  const r = (raw ?? {}) as Raw;
  return {
    id: str(r.id),
    description: str(r.description),
    amountMinor: num(pick(r, "amount_minor", "amountMinor")),
    itemType: (pick(r, "item_type", "itemType") ?? "booking") as OrderLineItem["itemType"],
  };
}

export function mapOrder(raw: unknown): Order {
  const r = (raw ?? {}) as Raw;
  const items = r.items;
  return {
    id: str(r.id),
    ref: str(r.ref),
    paymentId: (optStr(pick(r, "payment_id", "paymentId")) ?? null),
    customerName: str(pick(r, "customer_name", "customerName")),
    customerEmail: optStr(pick(r, "customer_email", "customerEmail")) ?? undefined,
    source: str(r.source) as OrderSource,
    sourceLabel: str(pick(r, "source_label", "sourceLabel")),
    provider: (r.provider ?? null) as OrderProvider,
    totalMinor: num(pick(r, "total_minor", "totalMinor")),
    refundedMinor: num(pick(r, "refunded_minor", "refundedMinor")),
    currency: str(r.currency) || "USD",
    status: str(r.status) as OrderStatus,
    createdAt: str(pick(r, "created_at", "createdAt")),
    paymentRef: optStr(pick(r, "payment_ref", "paymentRef")),
    receiptUrl: optStr(pick(r, "receipt_url", "receiptUrl")),
    ...(Array.isArray(items) ? { items: items.map(mapLineItem) } : {}),
  };
}

function mapKpis(raw: unknown): OrdersKpis {
  const r = (raw ?? {}) as Raw;
  return {
    grossMinor: num(pick(r, "gross_minor", "grossMinor")),
    refundedMinor: num(pick(r, "refunded_minor", "refundedMinor")),
    netMinor: num(pick(r, "net_minor", "netMinor")),
    count: num(r.count),
    currency: str(r.currency) || "USD",
    reservedMinor: optNum(pick(r, "reserved_minor", "reservedMinor")),
    reservedCount: optNum(pick(r, "reserved_count", "reservedCount")),
  };
}

function mapOrdersPage(raw: unknown): OrdersPage {
  const r = (raw ?? {}) as Raw;
  const rows = Array.isArray(r.orders) ? r.orders : Array.isArray(r.data) ? r.data : [];
  return {
    orders: rows.map(mapOrder),
    total: num(r.total),
    page: optNum(r.page),
    pageSize: optNum(pick(r, "page_size", "pageSize")),
    pages: optNum(r.pages),
    kpis: mapKpis(r.kpis),
  };
}

function mapCollectResult(raw: unknown): CollectResult {
  const r = (raw ?? {}) as Raw;
  return {
    orderId: str(pick(r, "order_id", "orderId")),
    status: str(r.status) as OrderStatus,
    collectedMinor: num(pick(r, "collected_minor", "collectedMinor")),
    method: (r.method ?? undefined) as CollectMethod | undefined,
    paymentRef: optStr(pick(r, "payment_ref", "paymentRef")),
  };
}

/** cap: billing.read · scope: org — the transactions list (filters + paging + KPIs). */
export const listOrders = (params: { status?: string | null; source?: string | null; search?: string | null; page?: number; pageSize?: number } = {}) =>
  tag("listOrders", apiRaw<unknown>(
    `/v1/admin/orders${q({
      status: params.status ?? undefined,
      source: params.source ?? undefined,
      search: params.search ?? undefined,
      page: params.page ? String(params.page) : undefined,
      page_size: params.pageSize ? String(params.pageSize) : undefined,
    })}`, undefined, "listAdminOrders").then(mapOrdersPage));

/** cap: billing.read · scope: org — one order with line items + provider refs. */
export const getOrder = (id: string) =>
  tag("getOrder", apiRaw<unknown>(`/v1/admin/orders/${encodeURIComponent(id)}`, undefined, "getAdminOrder")
    .then((r) => mapOrder(r && typeof r === "object" && "order" in (r as Raw) ? (r as Raw).order : r)));

/**
 * cap: billing.write · scope: org — refund an order. `amountMinor` omitted =
 * full refund; the server reverses the platform application fee proportionally
 * (never the FE). `idempotencyKey` makes the action safe to retry.
 */
export const refundOrder = (input: { order: Order; amountMinor?: number; reason?: string; idempotencyKey: string }) =>
  tag("refundOrder", apiRaw<RefundResult>(`/v1/admin/refunds`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      payment_id: input.order.paymentId,
      amount_minor: input.amountMinor,          // omit = full
      reason: input.reason,
      idempotency_key: input.idempotencyKey,
    }),
  }));

/**
 * cap: billing.write · scope: org — refund by payment id directly (used by the
 * guest / registration cancel+refund flow, which has the payment id + amount
 * but not a full Order). Same op as refundOrder; the server derives the
 * connected account + application-fee reversal.
 */
export const refundPayment = (input: { paymentId: string; amountMinor?: number; reason?: string; idempotencyKey: string }) =>
  tag("refundPayment", apiRaw<RefundResult>(`/v1/admin/refunds`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      payment_id: input.paymentId,
      amount_minor: input.amountMinor,   // omit = full
      reason: input.reason,
      idempotency_key: input.idempotencyKey,
    }),
  }));

/**
 * cap: billing.write · scope: org — collect payment on a RESERVED (on-site /
 * pay-at-door) order. The FE names the order + method (+ optional partial); the
 * server records the collection, moves reserved → paid, and drives the provider
 * for card paths. The FE never computes or moves money. Adopts the payments
 * worker's collect-balance semantics (POST /v1/admin/orders/:id/collect).
 */
export const collectOrder = (input: { orderId: string; method: CollectMethod; amountMinor?: number; idempotencyKey: string }) =>
  tag("collectOrder", apiRaw<unknown>(`/v1/admin/orders/${encodeURIComponent(input.orderId)}/collect`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ method: input.method, amount_minor: input.amountMinor, idempotency_key: input.idempotencyKey }),
  }, "collectAdminOrder").then(mapCollectResult));

/** Idempotency-key generator for the refund action (no Date.now import needed). */
export const newIdempotencyKey = (): string =>
  `rf_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;

/** Render helper for server-derived minor-unit amounts. Display only. */
export function formatMinor(amountMinor: number, currency: string): string {
  try {
    if (!Number.isFinite(amountMinor)) amountMinor = 0;
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}
